import type { plugInToolbarActionParams } from '@wix/bi-logger-rce-data/v2/types';
import lodash from 'lodash';

import type * as T from './../../bundled-dependencies/wix-bi-logger-rich-editor';
import type { Logger } from './../../bundled-dependencies/wix-web-bi-logger';
import { isSSR } from './../../wix-rich-content-common';

import type { DefaultBIParams } from '..';
/* eslint-disable no-console*/
import type { BISettings, WebBiLogger } from '../WixRicosTypes';

export interface GenericParams {
  container: BISettings['consumer'];
  container_id: BISettings['containerId'];
  container_platform: BISettings['platform'];
  container_usage: BISettings['usage'];
  post_id: BISettings['postId'];
  rce_session_id: string;
  msid: string;
  wixRicosVersion: string;
  version: string; // ricos version
}

interface RicosBILoggerArgs {
  webBiLogger: WebBiLogger | undefined;
  logger?: Logger;
  genericParams: GenericParams;
  essentials: DefaultBIParams;
}

type AddUndefined<T> = {
  [P in keyof T]: T[P] | undefined;
};

type EventFunctions =
  | typeof T.addPlugin
  | typeof T.addPluginStep
  | typeof T.addPluginSuccess
  | typeof T.changePlugin
  | typeof T.contentAdded
  | typeof T.deletePlugin
  | typeof T.inlineToolbarOpened
  | typeof T.keyboardShortcutAction
  | typeof T.menuLoad
  | typeof T.openEditorSuccess
  | typeof T.pluginModalOpened
  | typeof T.pluginsPopoverClick
  | typeof T.pluginsPopoverTabSwitch
  | typeof T.publishPost
  | typeof T.uploadMediaFinished
  | typeof T.uploadMediaTry
  | typeof T.viewerAction
  | typeof T.viewerLoaded
  | typeof T.addPluginLink
  | typeof T.searchForPlugin
  | typeof T.searchInsideThePlugin
  | typeof T.pluginTableColumnRowAction
  | typeof T.tablePluginAddColumnRow
  | typeof T.tablePluginDeleteColumnRow
  | typeof T.tablePluginClickOnOptionMenu
  | typeof T.tablePluginClickActionFromOptionMenu
  | typeof T.settingsModalOpenedForPlugin
  | typeof T.settingsModalClosedForPlugin;

type EventFunctionParameters<T extends EventFunctions> =
  | Omit<AddUndefined<Required<Parameters<T>[0]>>, keyof GenericParams>
  | GenericParams;

function paramsToBiFields(essentials: DefaultBIParams) {
  const {
    bsi,
    metaSiteId: msid,
    visitorId: _visitorId,
    appId: _appId,
    instanceId: _instanceId,
    siteOwnerId: _siteOwnerId,
  } = essentials;
  return lodash.pickBy(
    { bsi, msid, _visitorId, _appId, _instanceId, _siteOwnerId },
    lodash.identity
  );
}

export class RicosBILogger {
  newLoggerConfig: { logger: Logger; genericParams: Omit<GenericParams, 'msid'> } | undefined;

  oldLogger: Promise<Logger> | undefined;

  constructor({ logger, webBiLogger, genericParams, essentials = {} }: RicosBILoggerArgs) {
    if (logger) {
      this.newLoggerConfig = { logger, genericParams: lodash.omit(genericParams, 'msid') };
      return;
    }

    if (!isSSR()) {
      const biLoggerPromise = Promise.resolve(
        webBiLogger ||
          import(
            /* webpackChunkName: "wix-ricos-bi-logger-fallback" */ './../../bundled-dependencies/wix-web-bi-logger'
          ).then(result => result.webBiLogger)
      );

      this.oldLogger = biLoggerPromise.then(result => {
        // for esbuild consumers building esm bundle https://github.com/evanw/esbuild/issues/566#issuecomment-846707879 commonjs lazy chunks get double wrapped
        // @ts-expect-error - ignore
        const biLogger: typeof result = result.default?.default ? result.default : result;

        return biLogger
          .factory()
          .updateDefaults({ ...genericParams, ...paramsToBiFields(essentials) })
          .logger();
      });
    }
  }

  log<EventFunction extends EventFunctions>(
    func: EventFunction,
    params: EventFunctionParameters<EventFunction>
  ): void {
    if (this.newLoggerConfig) {
      const logObject = func(params);
      const { params: eventParams, ...rest } = logObject;
      this.newLoggerConfig.logger.report?.({
        params: { ...this.newLoggerConfig.genericParams, ...eventParams },
        ...rest,
      });
    } else if (this.oldLogger) {
      this.oldLogger.then(logger => logger.report(func(params)));
    } else {
      console.error(`[RicosBILogger.ts:log] RicosBILogger was not initialized`);
    }
  }

  logV2(params: plugInToolbarActionParams): void {
    const args = {
      evid: 101,
      src: 116,
      endpoint: 'rich-editor',
      params,
    };

    if (this.newLoggerConfig) {
      this.newLoggerConfig.logger.report?.(args);
    } else if (this.oldLogger) {
      this.oldLogger.then(logger => logger.report(args));
    } else {
      console.error(`[RicosBILogger.ts:log] RicosBILogger was not initialized`);
    }
  }
}
